import React, { useState, useEffect } from 'react';
import { Database, Plus, Trash2, Edit2, Table, Columns } from 'lucide-react';
import storageService from '../../services/storageService';

const DataSourceConfig = () => {
  const [dataSources, setDataSources] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingSource, setEditingSource] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    type: 'database',
    connectionString: '',
    tables: []
  });
  const [newTable, setNewTable] = useState({ name: '', columns: [] });
  const [newColumn, setNewColumn] = useState({ name: '', type: '', format: '' });

  useEffect(() => {
    loadDataSources();
  }, []);

  const loadDataSources = () => {
    const sources = storageService.getDataSources();
    setDataSources(sources);
  };

  const handleOpenModal = (source = null) => {
    if (source) {
      setEditingSource(source);
      setFormData(source);
    } else {
      setEditingSource(null);
      setFormData({
        name: '',
        type: 'database',
        connectionString: '',
        tables: []
      });
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingSource(null);
    setNewTable({ name: '', columns: [] });
    setNewColumn({ name: '', type: '', format: '' });
  };

  const handleSave = () => {
    const source = {
      ...formData,
      id: editingSource?.id || `ds${Date.now()}`,
      createdAt: editingSource?.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    storageService.saveDataSource(source);
    loadDataSources();
    handleCloseModal();
  };

  const handleDelete = (id) => {
    if (window.confirm('Bạn có chắc muốn xóa nguồn dữ liệu này?')) {
      storageService.deleteDataSource(id);
      loadDataSources();
    }
  };

  const handleAddColumn = () => {
    if (newColumn.name && newColumn.type && newColumn.format) {
      setNewTable({
        ...newTable,
        columns: [...newTable.columns, { ...newColumn }]
      });
      setNewColumn({ name: '', type: '', format: '' });
    }
  };

  const handleRemoveColumn = (index) => {
    setNewTable({
      ...newTable,
      columns: newTable.columns.filter((_, i) => i !== index)
    });
  };

  const handleAddTable = () => {
    if (newTable.name && newTable.columns.length > 0) {
      setFormData({
        ...formData,
        tables: [...formData.tables, { ...newTable }]
      });
      setNewTable({ name: '', columns: [] });
    }
  };

  const handleRemoveTable = (index) => {
    setFormData({
      ...formData,
      tables: formData.tables.filter((_, i) => i !== index)
    });
  };

  const getTypeIcon = (type) => {
    switch(type) {
      case 'database': return <Database size={18} />;
      case 'file': return <Table size={18} />;
      case 'api': return <Columns size={18} />;
      default: return <Database size={18} />;
    }
  };

  const getTypeBadge = (type) => {
    const badges = {
      database: { label: 'Database', className: 'badge-info' },
      file: { label: 'File', className: 'badge-warning' },
      api: { label: 'API', className: 'badge-success' }
    };
    const badge = badges[type] || badges.database;
    return <span className={`badge ${badge.className}`}>{badge.label}</span>;
  };

  return (
    <div className="data-source-config">
      <div className="header">
        <h2><Database size={28} /> Quản lý Nguồn Dữ liệu</h2>
        <button className="btn btn-primary" onClick={() => handleOpenModal()}>
          <Plus size={18} /> Thêm Nguồn
        </button>
      </div>

      <div className="data-source-list">
        {dataSources.length === 0 ? (
          <div className="empty-state">
            <Database size={64} />
            <h3>Chưa có nguồn dữ liệu</h3>
            <p>Bắt đầu bằng cách thêm nguồn dữ liệu đầu tiên của bạn</p>
            <button className="btn btn-primary" onClick={() => handleOpenModal()}>
              <Plus size={18} /> Thêm nguồn đầu tiên
            </button>
          </div>
        ) : (
          <div className="cards-grid">
            {dataSources.map(source => {
              const totalColumns = source.tables?.reduce((sum, t) => sum + (t.columns?.length || 0), 0) || 0;
              const formattedDate = source.updatedAt 
                ? new Date(source.updatedAt).toLocaleDateString('vi-VN', { 
                    year: 'numeric', 
                    month: 'short', 
                    day: 'numeric' 
                  })
                : 'Chưa cập nhật';

              return (
                <div key={source.id} className="card card-source">
                  <div className="card-source-header">
                    <div className={`source-icon source-icon-${source.type}`}>
                      {getTypeIcon(source.type)}
                    </div>
                    <div className="source-info">
                      <h3>{source.name}</h3>
                      <div className="source-meta">
                        {getTypeBadge(source.type)}
                        <span className="meta-divider">•</span>
                        <span className="meta-text">{formattedDate}</span>
                      </div>
                    </div>
                    <div className="card-actions">
                      <button 
                        className="btn-icon" 
                        onClick={() => handleOpenModal(source)}
                        title="Sửa"
                      >
                        <Edit2 size={16} />
                      </button>
                      <button 
                        className="btn-icon btn-danger" 
                        onClick={() => handleDelete(source.id)}
                        title="Xóa"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>

                  <div className="source-stats">
                    <div className="stat-item">
                      <div className="stat-icon">
                        <Table size={20} />
                      </div>
                      <div className="stat-content">
                        <div className="stat-value">{source.tables?.length || 0}</div>
                        <div className="stat-label">Bảng</div>
                      </div>
                    </div>
                    <div className="stat-divider"></div>
                    <div className="stat-item">
                      <div className="stat-icon">
                        <Columns size={20} />
                      </div>
                      <div className="stat-content">
                        <div className="stat-value">{totalColumns}</div>
                        <div className="stat-label">Cột</div>
                      </div>
                    </div>
                  </div>

                  <div className="source-connection">
                    <label>Connection:</label>
                    <code>{source.connectionString}</code>
                  </div>

                  {source.tables && source.tables.length > 0 && (
                    <div className="source-tables-preview">
                      <div className="tables-preview-label">
                        <Table size={14} />
                        <span>Tables ({source.tables.length})</span>
                      </div>
                      <div className="tables-preview-list">
                        {source.tables.slice(0, 3).map((table, idx) => (
                          <span key={idx} className="table-tag">
                            {table.name}
                          </span>
                        ))}
                        {source.tables.length > 3 && (
                          <span className="table-tag-more">+{source.tables.length - 3}</span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal modal-large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>
                <Database size={24} />
                {editingSource ? 'Sửa Nguồn Dữ liệu' : 'Thêm Nguồn Dữ liệu'}
              </h3>
              <button className="btn-close" onClick={handleCloseModal}>×</button>
            </div>

            <div className="modal-body">
              <div className="form-group">
                <label>Tên nguồn <span className="required">*</span></label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="VD: Core Banking Database"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Loại nguồn <span className="required">*</span></label>
                  <select
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                  >
                    <option value="database">Database</option>
                    <option value="file">File</option>
                    <option value="api">API</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Connection String <span className="required">*</span></label>
                  <input
                    type="text"
                    value={formData.connectionString}
                    onChange={(e) => setFormData({ ...formData, connectionString: e.target.value })}
                    placeholder="VD: postgresql://host:port/database"
                  />
                </div>
              </div>

              <div className="form-section">
                <h4><Table size={20} /> Bảng dữ liệu</h4>
                
                {formData.tables.length > 0 && (
                  <div style={{marginBottom: '1.5rem'}}>
                    {formData.tables.map((table, idx) => (
                      <div key={idx} style={{
                        padding: '1.25rem',
                        background: 'white',
                        borderRadius: 'var(--radius)',
                        border: '1.5px solid var(--border)',
                        marginBottom: '1rem',
                        boxShadow: 'var(--shadow-xs)'
                      }}>
                        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem'}}>
                          <div style={{flex: 1}}>
                            <div style={{display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem'}}>
                              <Table size={18} style={{color: 'var(--ey-black)'}} />
                              <strong style={{fontSize: '1.125rem', color: 'var(--text)'}}>{table.name}</strong>
                              <span className="badge badge-gray">{table.columns.length} cột</span>
                            </div>
                          </div>
                          <button
                            className="btn-icon btn-danger"
                            onClick={() => handleRemoveTable(idx)}
                            title="Xóa bảng"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>

                        {/* Columns list */}
                        <div style={{
                          display: 'grid',
                          gap: '0.5rem',
                          padding: '1rem',
                          background: 'var(--gray-50)',
                          borderRadius: 'var(--radius-sm)',
                          border: '1px solid var(--border-light)'
                        }}>
                          <div style={{
                            display: 'flex',
                            gap: '0.5rem',
                            paddingBottom: '0.5rem',
                            borderBottom: '1px solid var(--border)',
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            color: 'var(--text-light)',
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em'
                          }}>
                            <div style={{flex: '2'}}>Tên cột</div>
                            <div style={{flex: '1'}}>Kiểu</div>
                            <div style={{flex: '1.5'}}>Format</div>
                          </div>
                          {table.columns.map((col, colIdx) => (
                            <div key={colIdx} style={{
                              display: 'flex',
                              gap: '0.5rem',
                              alignItems: 'center',
                              padding: '0.5rem',
                              background: 'white',
                              borderRadius: 'var(--radius-sm)',
                              fontSize: '0.875rem'
                            }}>
                              <code style={{flex: '2', fontSize: '0.8125rem'}}>{col.name}</code>
                              <span className="badge badge-info" style={{flex: '1', fontSize: '0.75rem'}}>
                                {col.type}
                              </span>
                              <span style={{flex: '1.5', fontSize: '0.8125rem', color: 'var(--text-light)'}}>
                                {col.format}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                <div style={{
                  padding: '1.5rem',
                  background: 'white',
                  borderRadius: 'var(--radius)',
                  border: '2px dashed var(--border)'
                }}>
                  <div className="form-group">
                    <label>Tên bảng</label>
                    <input
                      type="text"
                      value={newTable.name}
                      onChange={(e) => setNewTable({ ...newTable, name: e.target.value })}
                      placeholder="VD: accounts, transactions"
                    />
                  </div>

                  <div style={{marginBottom: '1rem'}}>
                    <label style={{display: 'block', marginBottom: '0.75rem', fontWeight: 600}}>
                      <Columns size={16} style={{display: 'inline', marginRight: '0.5rem'}} />
                      Cột dữ liệu
                    </label>
                    
                    {newTable.columns.length > 0 && (
                      <div style={{marginBottom: '1rem'}}>
                        {newTable.columns.map((col, idx) => (
                          <div key={idx} style={{
                            display: 'flex',
                            gap: '0.5rem',
                            padding: '0.625rem 0.875rem',
                            background: 'var(--gray-50)',
                            borderRadius: 'var(--radius-sm)',
                            marginBottom: '0.5rem',
                            alignItems: 'center'
                          }}>
                            <code style={{flex: 1, fontSize: '0.875rem'}}>{col.name}</code>
                            <span className="badge badge-info" style={{fontSize: '0.75rem'}}>{col.type}</span>
                            <span style={{fontSize: '0.8125rem', color: 'var(--text-light)'}}>{col.format}</span>
                            <button
                              className="btn-icon btn-danger"
                              style={{width: '28px', height: '28px'}}
                              onClick={() => handleRemoveColumn(idx)}
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}

                    <div className="form-row">
                      <input
                        type="text"
                        value={newColumn.name}
                        onChange={(e) => setNewColumn({ ...newColumn, name: e.target.value })}
                        placeholder="Tên cột"
                      />
                      <select
                        value={newColumn.type}
                        onChange={(e) => setNewColumn({ ...newColumn, type: e.target.value })}
                      >
                        <option value="">Chọn kiểu</option>
                        <option value="string">String</option>
                        <option value="number">Number</option>
                        <option value="date">Date</option>
                        <option value="boolean">Boolean</option>
                      </select>
                      <input
                        type="text"
                        value={newColumn.format}
                        onChange={(e) => setNewColumn({ ...newColumn, format: e.target.value })}
                        placeholder="Format (VD: VARCHAR(50))"
                      />
                      <button
                        className="btn btn-secondary"
                        onClick={handleAddColumn}
                        disabled={!newColumn.name || !newColumn.type || !newColumn.format}
                      >
                        <Plus size={16} /> Thêm cột
                      </button>
                    </div>
                  </div>

                  <button
                    className="btn btn-primary"
                    onClick={handleAddTable}
                    disabled={!newTable.name || newTable.columns.length === 0}
                  >
                    <Plus size={18} /> Thêm bảng
                  </button>
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={handleCloseModal}>
                Hủy
              </button>
              <button 
                className="btn btn-primary" 
                onClick={handleSave}
                disabled={!formData.name || !formData.connectionString || formData.tables.length === 0}
              >
                {editingSource ? 'Cập nhật' : 'Lưu'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DataSourceConfig;
